import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* -- Neural Particle Network -- */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 55;
    const CONNECT_DIST = 120;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* -- Profile Card -- */
function ProfileCard({ title, chemistry, language }) {
  return (
    <div className="calc-thesis-box" style={{ marginBottom: '1rem' }}>
      <h3 style={{ color: '#c9a84c', fontFamily: 'Comfortaa, sans-serif', fontSize: '1rem', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>{title}</h3>
      <p style={{ color: '#999', fontSize: '0.9rem', marginBottom: '0.4rem' }}>{chemistry}</p>
      <p style={{ color: '#ccc', fontSize: '0.9rem', fontStyle: 'italic' }}>Language: {language}</p>
    </div>
  );
}

/* -- Treatment Row -- */
function TreatmentRow({ condition, treatment, effect }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '1rem', padding: '1rem 0', borderBottom: '1px solid rgba(201,168,76,0.08)' }}>
      <span style={{ color: '#c9a84c', fontFamily: 'Comfortaa, sans-serif', fontSize: '0.85rem' }}>{condition}</span>
      <span style={{ color: '#e0e0e0', fontSize: '0.9rem' }}>{treatment}</span>
      <span style={{ color: '#999', fontSize: '0.9rem' }}>{effect}</span>
    </div>
  );
}

/* -- Main Component -- */
function SyntheticProfiles() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">NeuPharma &middot; Drug Discovery</span>
          <h1>Synthetic Neurochemical Profiles</h1>
          <p className="calculus-hero-sub">One model. Frozen weights. The chemistry changes. The language changes. The weights never do.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <blockquote>
              Drugs don't rewire your neurons. They change the chemistry. The chemistry changes the processing. The behavior changes. The structure stays the same.
            </blockquote>
          </section>

          <Pulse />

          {/* The Insight */}
          <section className="calc-section reveal">
            <h2>The Insight</h2>
            <p>
              The Neurochemical Cascade Architecture processes language through 34 attention heads
              mapped one-to-one to the human neurochemical biosynthetic pathway. Each head is gated by
              Hodgkin-Huxley conductance dynamics with real pharmacokinetic half-lives. The model is
              trained once. The weights are frozen.
            </p>
            <p>
              Then you inject a compound's receptor profile. The chemistry shifts. The gates open
              differently. The language output changes. Not because you prompted it. Because the
              conductance changed.
            </p>

            <div className="calc-thesis-box">
              <p>
                The model weights are the structure. The neurochemical state is the chemistry.
                The language is the behavior.
              </p>
            </div>
          </section>

          <Pulse />

          {/* How It Works */}
          <section className="calc-section reveal">
            <h2>Signal Flow</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Pharmacokinetic Transduction</span>
              <code>Compound &rarr; Receptor Profile &rarr; PK Simulator &rarr; 8D State Vector &rarr; NCA Cascade &rarr; Language</code>
              <span className="calc-eq-desc">The model doesn't simulate the drug. It responds to the drug.</span>
            </div>

            <p>
              A pharmacokinetic simulator models the full blood chemistry response to any compound.
              It collapses that response into an 8-dimensional neurochemical state vector: dopamine,
              norepinephrine, serotonin, GABA, oxytocin, cortisol, endorphin, acetylcholine.
            </p>
            <p>
              That vector injects into the NCA's neurotransmitter heads as phasic stimulus. The
              cascade's own biological feedback mechanisms handle the rest: receptor desensitization,
              vesicle depletion, autoreceptor feedback, antagonistic pair interactions,
              pharmacokinetic decay.
            </p>
            <p>
              The output is genuine because it emerges from altered conductance, not from a prompt.
              Every gate value, every head activation, every feedback mechanism is logged.
              176 bytes of auditable neurochemical state per timestep.
            </p>
          </section>

          <Pulse />

          {/* Condition Profiles */}
          <section className="calc-section reveal">
            <h2>Condition Profiles</h2>
            <p>
              Every neurological and psychiatric condition has a known neurochemical signature.
              Load the profile. The AI responds from inside that chemistry.
            </p>

            <ProfileCard
              title="PTSD"
              chemistry="Elevated cortisol baseline. Hyperactive norepinephrine. Depleted serotonin. Amygdala locked open. Hippocampus suppressed."
              language="Hypervigilant, fragmented, avoidant. Memory without context."
            />
            <ProfileCard
              title="Bipolar (Manic Phase)"
              chemistry="Dopamine flooded. Norepinephrine elevated. Serotonin unstable. No cortisol brake."
              language="Pressured, grandiose, flight of ideas. Racing associations."
            />
            <ProfileCard
              title="Bipolar (Depressive Phase)"
              chemistry="Dopamine crashed. Serotonin depleted. Cortisol elevated."
              language="Flat, sparse, hopeless. Anhedonic withdrawal."
            />
            <ProfileCard
              title="Autism Spectrum"
              chemistry="Serotonin transport reduced. Oxytocin baseline deficit. GABA/glutamate imbalanced."
              language="Literal, pattern-focused, reduced social prosody."
            />
            <ProfileCard
              title="Generalized Anxiety"
              chemistry="Cortisol chronically elevated. GABA deficit. Norepinephrine hyperactive."
              language="Catastrophizing, reassurance-seeking, rumination patterns."
            />
            <ProfileCard
              title="Major Depression"
              chemistry="Serotonin depleted. Dopamine blunted. Cortisol elevated. Endorphin suppressed."
              language="Anhedonic, self-critical, withdrawn."
            />
          </section>

          <Pulse />

          {/* Treatment Response */}
          <section className="calc-section reveal">
            <h2>Treatment Response Modeling</h2>
            <p>
              Load a condition profile. Apply a treatment. Watch the chemistry shift. Watch the language change.
            </p>

            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '1rem', padding: '0.8rem 0', borderBottom: '1px solid rgba(201,168,76,0.2)' }}>
                <span style={{ color: '#c9a84c', fontFamily: 'Comfortaa, sans-serif', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Condition</span>
                <span style={{ color: '#c9a84c', fontFamily: 'Comfortaa, sans-serif', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Treatment</span>
                <span style={{ color: '#c9a84c', fontFamily: 'Comfortaa, sans-serif', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Cascade Effect</span>
              </div>

              <TreatmentRow
                condition="PTSD"
                treatment="SSRI"
                effect="Serotonin reuptake blocked. Serotonin accumulates. Cortisol attenuates via antagonistic pair. Amygdala gate narrows. Language shifts from hypervigilant to regulated."
              />
              <TreatmentRow
                condition="Bipolar (Manic)"
                treatment="Lithium"
                effect="Dopamine and norepinephrine damped. Serotonin stabilized. Pressured speech decelerates. Grandiosity fades."
              />
              <TreatmentRow
                condition="Anxiety"
                treatment="Benzodiazepine"
                effect="GABA conductance amplified. Cortisol suppressed. Norepinephrine attenuated. Catastrophizing reduces. Sentence structure stabilizes."
              />
              <TreatmentRow
                condition="Depression"
                treatment="SNRI"
                effect="Serotonin and norepinephrine reuptake blocked. Both accumulate. Dopamine secondary lift. Engagement returns. Self-referential negativity decreases."
              />
              <TreatmentRow
                condition="Autism"
                treatment="Oxytocin (intranasal)"
                effect="Oxytocin head directly stimulated. Social gating in T3 regions shifts. Insula and anterior cingulate activation increases. Social prosody in language output rises."
              />
            </div>
          </section>

          <Pulse />

          {/* The Equation */}
          <section className="calc-section reveal">
            <h2>Why This Works</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">State Evolution (per head)</span>
              <code>dS/dt = &minus;&lambda;(S &minus; E) + stimulus + tonic</code>
              <span className="calc-eq-desc">Chemistry evolves. Weights don't. Behavior emerges from conductance.</span>
            </div>

            <p>
              Every head in the cascade maintains persistent neurochemical state that evolves
              according to pharmacokinetic differential equations. The decay constant &lambda; is
              derived from published half-lives. The equilibrium potential E is the biological baseline.
              The stimulus is whatever you inject: a drug, a voice, a conversation.
            </p>
            <p>
              Six biological feedback mechanisms operate on every head simultaneously:
              mean-reverting decay, concentration-dependent clearance, autoreceptor negative feedback,
              receptor desensitization, vesicle pool depletion, and ATP energy cycling.
            </p>
            <p>
              This is not a lookup table. This is not a prompt. This is a dynamical system that
              responds to pharmacological input the way a nervous system responds. And the language
              that emerges from the altered state is genuine, because the gates that shape
              attention are physically different.
            </p>
          </section>

          <Pulse />

          {/* The Auditability */}
          <section className="calc-section reveal">
            <h2>Auditable by Design</h2>

            <div className="calc-thesis-box">
              <p>
                176 bytes of neurochemical state per timestep. Every gate value. Every head activation.
                Every feedback mechanism. Reproducible. Explainable. Every transition from condition
                to treatment to language output carries a complete chemical audit trail.
              </p>
            </div>

            <p>
              No black box. No hidden layers of sentiment. The audit trail IS the computation.
              Feed in the compound. Read the chemistry. Read the language. Trace every step.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section reveal">
            <h2>It Was Always Chemistry</h2>
            <p>
              Claude Shannon gave us the calculus of surprise. The calculus of meaning was
              always biochemistry. For 78 years, no one put it back.
            </p>

            <blockquote>
              Computer science labeled emotions. We wrote the differential equations.
            </blockquote>

            <p style={{ textAlign: 'center', marginTop: '2rem' }}>
              <a
                href="mailto:caelsereith@aetherprotocols.com"
                style={{
                  display: 'inline-block',
                  padding: '0.8rem 2rem',
                  fontFamily: 'Comfortaa, sans-serif',
                  fontSize: '0.85rem',
                  letterSpacing: '0.15em',
                  color: '#c9a84c',
                  border: '1px solid rgba(201,168,76,0.3)',
                  background: 'transparent',
                  textDecoration: 'none',
                  borderRadius: '100px',
                  transition: 'all 0.3s ease',
                }}
              >
                Partner With Us
              </a>
            </p>
          </section>

          <Pulse />

          {/* Patent Footer */}
          <section className="calc-section reveal" style={{ textAlign: 'center', opacity: 0.5 }}>
            <p style={{ fontFamily: 'Comfortaa, sans-serif', fontSize: '0.7rem', letterSpacing: '0.1em', color: '#666' }}>
              Patent Pending: 63/939,190 &middot; 63/962,385 &middot; 63/988,485
            </p>
            <p style={{ fontFamily: 'Comfortaa, sans-serif', fontSize: '0.65rem', letterSpacing: '0.1em', color: '#444', marginTop: '0.5rem' }}>
              Nexus Concordat Inc. &middot; NeuPharma Division &middot; 2026
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}

export default SyntheticProfiles;
